import { useCallback, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useLoanId from 'hooks/useLoanId';

import { getLoanCicHistoryAction } from 'store/loanApplications/actions';

/** Loads the loan's CIC history. `reload` keeps the current history on screen while it refreshes. */
export const useLoanCicHistory = () => {
  const dispatch = useDispatch();
  const loanId = useLoanId();

  const [history, setHistory] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  const latestRequestRef = useRef(0);

  const reload = useCallback(async () => {
    if (!loanId) {
      return;
    }

    latestRequestRef.current += 1;
    const requestId = latestRequestRef.current;

    setIsLoading(true);

    try {
      const data = await dispatch(getLoanCicHistoryAction({ loanId }));

      if (requestId === latestRequestRef.current) {
        setHistory(data);
        setIsError(false);
      }
    } catch {
      if (requestId === latestRequestRef.current) {
        setIsError(true);
      }
    } finally {
      if (requestId === latestRequestRef.current) {
        setIsLoading(false);
      }
    }
  }, [dispatch, loanId]);

  useEffectSafeStrictMode(() => {
    reload();
  }, [reload]);

  return { history, isLoading, isError, reload };
};
